/**
 * KVKK / GDPR cookie consent store.
 *
 * Single source of truth for the user's cookie preferences. The state
 * lives in localStorage (versioned) so we can re-prompt the user when
 * the policy materially changes. Consumers either:
 *
 *   • Call ``getConsent()`` once at module init (e.g. ``logEvent``).
 *   • Use the ``useCookieConsent()`` hook for live-reactive UI.
 *
 * KVKK 5(1) + Art. 7 GDPR notes baked into the design:
 *
 *   1. Non-essential categories default to ``false``. The user must
 *      ACTIVELY opt-in — pre-checked toggles are forbidden.
 *   2. "Reject All" must be as easy as "Accept All" — the banner gives
 *      both buttons equal visual weight.
 *   3. ``hasUserDecided()`` returns ``false`` until the user clicks an
 *      action button. Dismissing the banner via ``X`` does NOT count
 *      as consent (the banner re-shows on next page load).
 *   4. A version stamp lets us re-prompt when categories change. Bump
 *      ``CONSENT_VERSION`` if you add a new category or materially
 *      change what an existing one tracks.
 */

export type CookieCategory = "essential" | "analytics" | "personalization";

export interface CookieConsent {
  /** Always ``true``. Cannot be disabled — covers auth, CSRF, locale. */
  essential: true;
  /** Sentry replay + Firebase Analytics + performance traces. */
  analytics: boolean;
  /** Recommendation models, watched-ticker heuristics. */
  personalization: boolean;
  /** ISO timestamp of the decision. Helps with audit trails. */
  decided_at: string | null;
  /** Schema version — re-prompts user when bumped. */
  version: number;
}

/** Bump this when categories change or policy text materially shifts. */
export const CONSENT_VERSION = 1;

const STORAGE_KEY = "playntrade_cookie_consent_v2";
const CONSENT_CHANGED_EVENT = "playntrade:cookie-consent-changed";

/** Default state before the user has decided: only essential is on. */
const DEFAULT_CONSENT: CookieConsent = {
  essential: true,
  analytics: false,
  personalization: false,
  decided_at: null,
  version: CONSENT_VERSION,
};

/**
 * Read the current consent from localStorage. Returns the default
 * (analytics=false, personalization=false) if nothing is stored, or
 * if the stored version is older than ``CONSENT_VERSION`` (re-prompt
 * path). Never throws — falls back to defaults on parse errors.
 */
export function getConsent(): CookieConsent {
  if (typeof window === "undefined") return DEFAULT_CONSENT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (typeof parsed.version !== "number" || parsed.version < CONSENT_VERSION) {
      // Schema bumped — treat as un-decided so the banner re-shows.
      return DEFAULT_CONSENT;
    }
    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      personalization: Boolean(parsed.personalization),
      decided_at: parsed.decided_at ?? null,
      version: CONSENT_VERSION,
    };
  } catch {
    return DEFAULT_CONSENT;
  }
}

/** ``true`` once the user has clicked Accept / Reject / Save preferences. */
export function hasUserDecided(): boolean {
  if (typeof window === "undefined") return true; // SSR: never prompt server-side
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (typeof parsed.version !== "number" || parsed.version < CONSENT_VERSION) {
      return false;
    }
    return Boolean(parsed.decided_at);
  } catch {
    return false;
  }
}

/**
 * Persist a new consent decision + broadcast a change event so live
 * subscribers (the ``useCookieConsent`` hook) can re-render.
 */
export function setConsent(
  next: Pick<CookieConsent, "analytics" | "personalization">
): CookieConsent {
  const updated: CookieConsent = {
    essential: true,
    analytics: Boolean(next.analytics),
    personalization: Boolean(next.personalization),
    decided_at: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      window.dispatchEvent(
        new CustomEvent<CookieConsent>(CONSENT_CHANGED_EVENT, { detail: updated })
      );
    } catch {
      // localStorage may be disabled in strict-privacy browsers; we
      // intentionally fail-open (the rest of the app keeps working,
      // analytics just stays off because getConsent() returns defaults
      // next read).
    }
  }
  return updated;
}

/** Shortcut: opt-in to every non-essential category. */
export function acceptAll(): CookieConsent {
  return setConsent({ analytics: true, personalization: true });
}

/** Shortcut: opt-OUT of every non-essential category. */
export function rejectAll(): CookieConsent {
  return setConsent({ analytics: false, personalization: false });
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function subscribeToConsent(
  listener: (consent: CookieConsent) => void
): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<CookieConsent>).detail;
    if (detail) listener(detail);
  };
  window.addEventListener(CONSENT_CHANGED_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handler);
}

/**
 * Imperative request to open the preferences modal — used by the
 * "Cookie Settings" footer link. The banner component subscribes to
 * this event and toggles its open state.
 */
export const OPEN_PREFERENCES_EVENT = "playntrade:open-cookie-preferences";

export function requestOpenPreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}
